import React, { useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, Gift, Sparkles, ShoppingBag, Boxes, FileText, Users, LogOut, ExternalLink, Layout, UserCog, QrCode, Menu, X, Star, Tag, Truck, Mail, History, Activity, ShieldAlert, Settings as SettingsIcon } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const items = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true, perm: "analytics.view" },
  { to: "/admin/landing", icon: Layout, label: "Landing Page", perm: "landing.manage" },
  { to: "/admin/products", icon: Package, label: "Products", perm: "products.manage" },
  { to: "/admin/boxes", icon: Gift, label: "Pick Ah Box", perm: "boxes.manage" },
  { to: "/admin/flavours", icon: Sparkles, label: "Build Yuh Own Box", perm: "flavours.manage" },
  { to: "/admin/orders", icon: ShoppingBag, label: "Orders", perm: "orders.manage" },
  { to: "/admin/coupons", icon: Tag, label: "Coupons", perm: "orders.manage" },
  { to: "/admin/manifest", icon: Truck, label: "Delivery Manifest", perm: "orders.manage" },
  { to: "/admin/scanner", icon: QrCode, label: "Scanner", perm: "orders.scan" },
  { to: "/admin/inventory", icon: Boxes, label: "Inventory", perm: "inventory.manage" },
  { to: "/admin/stock-history", icon: History, label: "Stock history", perm: "inventory.manage" },
  { to: "/admin/reviews", icon: Star, label: "Reviews", perm: "reviews.moderate" },
  { to: "/admin/blog", icon: FileText, label: "Blog", perm: "blog.manage" },
  { to: "/admin/newsletter", icon: Mail, label: "Newsletter", perm: "analytics.view" },
  { to: "/admin/activity", icon: Activity, label: "Activity log", perm: "analytics.view" },
  { to: "/admin/users", icon: Users, label: "Users", perm: "users.manage" },
];

export default function AdminLayout() {
  const { user, logout, hasPermission, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/"); };
  const visibleItems = items.filter((it) => hasPermission(it.perm));
  const roleLabel = { super_admin: "Super Admin", admin: "Admin", staff: "Staff", customer: "Customer" }[user?.role] || user?.role;
  const closeSidebar = () => setSidebarOpen(false);

  const SidebarContent = () => (
    <>
      <div className="flex items-center justify-between mb-6 lg:mb-8">
        <Link to="/" className="flex items-center gap-2" onClick={closeSidebar}>
          <img src="/brand/fruit-blossoms.png" alt="Fruit Blossoms" className="h-9 lg:h-10 w-auto" />
          <div>
            <div className="font-display font-bold text-sm">Fruit Blossoms</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Backend</div>
          </div>
        </Link>
        <button className="lg:hidden p-2 rounded hover:bg-zinc-100" onClick={closeSidebar} data-testid="admin-sidebar-close">
          <X size={18} />
        </button>
      </div>
      <nav className="flex flex-col gap-1">
        {visibleItems.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end} onClick={closeSidebar}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${
                isActive ? "bg-[#F74B64] text-white" : "text-zinc-700 hover:bg-zinc-100"
              }`} data-testid={`admin-nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}>
            <it.icon size={16} className="flex-shrink-0" /> <span className="truncate">{it.label}</span>
          </NavLink>
        ))}
        <NavLink to="/admin/my-access" onClick={closeSidebar}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition mt-2 ${
              isActive ? "bg-[#F74B64] text-white" : "text-zinc-700 hover:bg-zinc-100"
            }`} data-testid="admin-nav-my-access">
          <UserCog size={16} className="flex-shrink-0" /> <span className="truncate">My Access</span>
        </NavLink>
        {(user?.role === "admin" || isSuperAdmin) && (
          <NavLink to="/admin/security" onClick={closeSidebar}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${
                isActive ? "bg-[#F74B64] text-white" : "text-zinc-700 hover:bg-zinc-100"
              }`} data-testid="admin-nav-security">
            <ShieldAlert size={16} className="flex-shrink-0" /> <span className="truncate">Security</span>
          </NavLink>
        )}
        {(user?.role === "admin" || isSuperAdmin) && (
          <NavLink to="/admin/settings" onClick={closeSidebar}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${
                isActive ? "bg-[#F74B64] text-white" : "text-zinc-700 hover:bg-zinc-100"
              }`} data-testid="admin-nav-settings">
            <SettingsIcon size={16} className="flex-shrink-0" /> <span className="truncate">Settings</span>
          </NavLink>
        )}
        {isSuperAdmin && (
          <NavLink to="/admin/maintenance" onClick={closeSidebar}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${
                isActive ? "bg-red-600 text-white" : "text-red-600 hover:bg-red-50"
              }`} data-testid="admin-nav-maintenance">
            <ShieldAlert size={16} className="flex-shrink-0" /> <span className="truncate">Maintenance</span>
          </NavLink>
        )}
      </nav>
      <div className="mt-8 border-t border-zinc-200 pt-4">
        <div className="text-xs text-zinc-500 mb-1 truncate">{user?.name}</div>
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: isSuperAdmin ? "#F74B64" : "#71717A" }}>
          {roleLabel}
        </div>
        <div className="mt-3 flex flex-col gap-1">
          <Link to="/" className="text-xs text-zinc-600 hover:text-zinc-900 flex items-center gap-1" onClick={closeSidebar}><ExternalLink size={12} /> View site</Link>
          <button onClick={doLogout} className="text-xs text-zinc-600 hover:text-red-500 flex items-center gap-1 text-left" data-testid="admin-logout">
            <LogOut size={12} /> Logout
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-layout">
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-zinc-200 flex items-center justify-between px-4 h-14">
        <button className="p-2 rounded hover:bg-zinc-100" onClick={() => setSidebarOpen(true)} data-testid="admin-sidebar-open">
          <Menu size={20} />
        </button>
        <Link to="/admin" className="flex items-center gap-2">
          <img src="/brand/fruit-blossoms.png" alt="Fruit Blossoms" className="h-8 w-auto" />
          <span className="font-display font-bold text-sm">Backend</span>
        </Link>
        <div className="w-9" />
      </header>

      {/* Mobile drawer overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-40"
          onClick={closeSidebar}
          data-testid="admin-sidebar-overlay"
        />
      )}

      <div className="flex">
        {/* Mobile drawer */}
        <aside
          className={`lg:hidden fixed top-0 left-0 h-full w-64 bg-white border-r border-zinc-200 p-5 z-50 transform transition-transform duration-200 ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } overflow-y-auto`}
          data-testid="admin-sidebar-mobile">
          <SidebarContent />
        </aside>

        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-60 min-h-screen bg-white border-r border-zinc-200 p-5 sticky top-0">
          <SidebarContent />
        </aside>

        <main className="flex-1 min-w-0 p-5 sm:p-6 lg:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
